/**
 * LifeReportResumeToast — KALKI 7.0 global "preparing your Life Report"
 * indicator.  Mounted inside App.js so every authenticated page sees it.
 *
 * The toast appears whenever the useLifeReportJob hook has a non-terminal
 * job snapshot (the hook itself polls — this component is purely visual).
 * On COMPLETED it morphs into "Your report is ready" with "View Report"
 * CTA for ~12 s, then auto-dismisses.
 *
 * Styling: subtle bottom-right pill, mobile-safe.
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Check, X } from "lucide-react";
import useLifeReportJob from "@/hooks/useLifeReportJob";

const INK = "#1C1C1C";
const INDIGO = "#5D00FF";

function pad(n) { return String(n).padStart(2, "0"); }
function fmtMMSS(sec) {
  if (sec == null || sec < 0) sec = 0;
  return `${pad(Math.floor(sec / 60))}:${pad(sec % 60)}`;
}

export default function LifeReportResumeToast() {
  const { job, status, remainingSeconds, stageLabel, reportId, dismiss }
    = useLifeReportJob();
  const nav = useNavigate();
  const [hidden, setHidden] = useState(false);
  const [autoCloseAt, setAutoCloseAt] = useState(null);

  // Reset "hidden" when a new job starts.
  useEffect(() => {
    if (job && status && status !== "COMPLETED") setHidden(false);
  }, [job?.job_id, status]);

  // Auto-close the "ready" toast 12 s after it appears.
  useEffect(() => {
    if (status === "COMPLETED" && !autoCloseAt) {
      setAutoCloseAt(Date.now() + 12_000);
    }
  }, [status, autoCloseAt]);
  useEffect(() => {
    if (!autoCloseAt) return;
    const t = setTimeout(() => { setHidden(true); dismiss(); }, 12_000);
    return () => clearTimeout(t);
  }, [autoCloseAt, dismiss]);

  if (hidden) return null;
  if (!job || !status) return null;
  if (status === "FAILED" || status === "REFUNDED") return null;   // no toast on failure
  // Hide when the user is already on the LifeReport page — the popup is visible there.
  if (typeof window !== "undefined" && window.location.pathname.startsWith("/app/report")) {
    return null;
  }

  const isReady = status === "COMPLETED";

  const base = {
    position: "fixed",
    bottom: 20, right: 20, zIndex: 1150,
    maxWidth: 320, padding: "10px 14px",
    borderRadius: 999,
    background: "white",
    boxShadow: "0 16px 44px rgba(10,8,30,0.22)",
    border: "1px solid rgba(93,0,255,0.1)",
    display: "flex", alignItems: "center", gap: 10,
    fontSize: 12.5, color: INK,
  };

  return (
    <div style={base}
         role="status"
         data-testid="life-report-resume-toast">
      {isReady ? (
        <span style={{
          width: 20, height: 20, borderRadius: 999,
          background: "#15803D", color: "white",
          display: "inline-flex", alignItems: "center", justifyContent: "center",
        }}>
          <Check size={12} />
        </span>
      ) : (
        <Loader2 size={16} style={{
          color: INDIGO, animation: "spin 1.4s linear infinite",
        }} />
      )}
      <div style={{ flex: 1, lineHeight: 1.35 }}>
        {isReady ? (
          <>
            <div style={{ fontWeight: 600 }}>Your Life Report is ready</div>
            <div style={{ fontSize: 11, color: "#676773" }}>Open from Profile anytime</div>
          </>
        ) : (
          <>
            <div><strong>Preparing your Life Report</strong> · {fmtMMSS(remainingSeconds)}</div>
            <div style={{ fontSize: 11, color: "#676773" }}>{stageLabel}</div>
          </>
        )}
      </div>
      {isReady ? (
        <button
          data-testid="life-report-resume-toast-view"
          onClick={() => { nav("/app/report?view=" + (reportId || "")); dismiss(); }}
          style={{
            background: INDIGO, color: "white", border: "none",
            padding: "6px 12px", borderRadius: 999,
            fontSize: 11.5, cursor: "pointer",
          }}>
          View
        </button>
      ) : (
        <button
          data-testid="life-report-resume-toast-dismiss"
          onClick={() => setHidden(true)}
          aria-label="Dismiss"
          style={{
            background: "transparent", border: "none",
            color: "#676773", padding: 4, cursor: "pointer",
          }}>
          <X size={14} />
        </button>
      )}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
